import {
    CategoryType,
    FieldUpdate,
    Inherited,
    InheritanceSource,
    Overridable,
    RealmCategorySpecificConfig,
} from '@/lib-backend';

/** Effective (resolved) value of an overridable config section. */
export function effective<T>(o: Overridable<T>): T {
    return o.Effective;
}

/** The value inherited from the parent (category/company), ignoring any override. */
export function inheritedValue<T>(o: Overridable<T>): T {
    return o.Inherited;
}

/** Value of an inherited-only (non-overridable) config section. */
export function inheritedOnly<T>(i: Inherited<T>): T {
    return i.Value;
}

/** Human-readable label for an inheritance source. */
export function sourceLabel(source: InheritanceSource): string {
    switch (source) {
        case InheritanceSource.Category:
            return 'Category';
        case InheritanceSource.Company:
            return 'Company';
        default:
            return 'Unknown';
    }
}

/**
 * Build a three-state FieldUpdate for an overridable realm section.
 * - not dirty              -> null             (leave the field untouched)
 * - dirty + overridden     -> { value: draft } (set/replace the realm override)
 * - dirty + not overridden -> { value: null }  (clear override, fall back to inherited)
 */
export function overrideUpdate<T>(
    dirty: boolean,
    overridden: boolean,
    draft: T
): FieldUpdate<T | null> {
    if (!dirty) return null;
    return { value: overridden ? draft : null };
}

type SkillSpecific = Extract<RealmCategorySpecificConfig, { type: CategoryType.SkillBasedGames }>;
type LuckySpecific = Extract<RealmCategorySpecificConfig, { type: CategoryType.LuckyBasedGames }>;

export function isSkillSpecific(cs: RealmCategorySpecificConfig): cs is SkillSpecific {
    return cs.type === CategoryType.SkillBasedGames;
}

export function isLuckySpecific(cs: RealmCategorySpecificConfig): cs is LuckySpecific {
    return cs.type === CategoryType.LuckyBasedGames;
}
